import { z } from "zod";

/**
 * Interface preferences (TW-3466) — the Settings → Preferences section. Language keeps its own
 * existing endpoint (PATCH /api/me/language); timezone + interface density are written together via
 * PATCH /api/me/preferences. One schema, shared by the web controls and the API ZodValidationPipe.
 * Owner prisma, self-scoped (a user only edits their own row).
 */

/** Interface density: the default comfortable spacing, or a compacted variant (drives `.dense`). */
export const DENSITIES = ["regular", "compact"] as const;
export type Density = (typeof DENSITIES)[number];
export const DEFAULT_DENSITY: Density = "regular";

/**
 * A curated timezone list for the picker (Spain-centric, plus common European + a few global).
 * The API additionally validates that any submitted value is a real IANA zone, so this list can
 * grow without a contract change.
 */
export const COMMON_TIMEZONES = [
  "Europe/Madrid",
  "Atlantic/Canary",
  "Europe/Lisbon",
  "Europe/London",
  "Europe/Dublin",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Rome",
  "Europe/Amsterdam",
  "Europe/Zurich",
  "Europe/Athens",
  "UTC",
  "America/New_York",
  "America/Los_Angeles",
] as const;

export const updatePreferencesSchema = z
  .object({
    // null clears the override (fall back to the app/browser default); a string is IANA-validated server-side.
    timezone: z.string().trim().min(1).max(64).nullable().optional(),
    density: z.enum(DENSITIES).optional(),
  })
  .refine((v) => v.timezone !== undefined || v.density !== undefined, {
    message: "provide at least one of: timezone, density",
  });

export type UpdatePreferencesInput = z.infer<typeof updatePreferencesSchema>;

/** The resolved preferences as persisted — returned by GET-via-/api/me and PATCH /api/me/preferences. */
export interface PreferencesDto {
  timezone: string | null;
  density: Density;
}
